'use client';

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faList } from '@fortawesome/pro-solid-svg-icons';
import { createPortal } from 'react-dom';
import Link from 'next/link';

const StrategyLists = ({ lists }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    isAbove: false,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const firstList = lists?.[0];
  const remainingLists = lists?.slice(1) || [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRemainingClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 192; // 12rem = 192px
      const dropdownHeight = 240; // max-h-60 = 240px + padding

      let top = rect.bottom + 4;
      const left = rect.right - dropdownWidth;
      let isAbove = false;

      // Check if dropdown would go off screen vertically
      if (top + dropdownHeight > window.innerHeight) {
        // Position above the trigger
        top = rect.top - 4;
        isAbove = true;
      }

      // If still off screen at top, position at bottom with max available space
      if (top < 0) {
        top = rect.bottom + 4;
        isAbove = false;
      }

      setDropdownPosition({
        top: top,
        left: left,
        isAbove: isAbove,
      });
    }

    setIsDropdownOpen(!isDropdownOpen);
  };

  if (!lists || lists.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 overflow-hidden">
      {/* First List */}
      {firstList && (
        <Link
          href={`/lists/my/${firstList.id}`}
          scroll={false}
          replace={false}
          prefetch={false}
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink"
        >
          <div className="px-1.5 py-1 bg-neutral-100 rounded-full flex justify-center items-center gap-0.5 overflow-hidden">
            <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
              <Icon icon={faList} className="text-neutral-600" size="xs" />
            </div>
            <div className="text-center justify-start text-tertiary text-xs font-normal font-['Inter'] leading-none truncate max-w-[80px]">
              {firstList.name || 'Unnamed List'}
            </div>
          </div>
        </Link>
      )}

      {/* Remaining Lists Toggle */}
      {remainingLists.length > 0 && (
        <div ref={triggerRef} className="relative flex-shrink-0">
          <div
            className="px-1.5 py-1 bg-neutral-100 rounded-full flex justify-center items-center gap-0.5 cursor-pointer hover:bg-neutral-200 transition-colors"
            onClick={handleRemainingClick}
          >
            <div className="text-center text-tertiary text-xs font-normal font-['Inter'] leading-none">
              +{remainingLists.length}
            </div>
          </div>

          {/* Dropdown */}
          {isDropdownOpen &&
            createPortal(
              <div
                ref={dropdownRef}
                className="fixed w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-2 z-[70]"
                style={{
                  ...(dropdownPosition.isAbove
                    ? { bottom: window.innerHeight - dropdownPosition.top }
                    : { top: dropdownPosition.top }),
                  left: dropdownPosition.left,
                }}
              >
                <div className="max-h-48 overflow-y-auto">
                  {remainingLists.map((list) => (
                    <Link
                      key={list.id}
                      href={`/lists/my/${list.id}`}
                      scroll={false}
                      replace={false}
                      prefetch={false}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDropdownOpen(false);
                      }}
                      className="block"
                    >
                      <div className="px-4 py-2 hover:bg-neutral-50 cursor-pointer text-sm text-neutral-700">
                        {list.name}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>,
              document.body
            )}
        </div>
      )}
    </div>
  );
};

export default StrategyLists;
